import { useCallback, useEffect, useState } from 'react';

import {
  listRecordedInvestments, listRecordableInvestors, recordInvestment,
  confirmFundsReceived, cancelInvestment,
  type RecordedInvestment, type RecordableInvestor,
} from '../../lib/closing';
import { listOpportunities, type AdminOpportunity } from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { formatMoney, formatDateShort, formatDateTime } from '../../lib/format';
import './AdminPages.css';

/**
 * Recorded investment closings.
 *
 * Deals are agreed off platform; this is where the result is entered. The admin does NOT
 * sign here, and there is no signature control on this screen at all. The investor
 * countersigns from their own portal, because a signature drawn in the issuer's session
 * is weak evidence of the investor's assent if the investment is ever disputed.
 *
 * The only financial figure an admin types is the share count. Amount and entry fee are
 * derived by the engine from the raise's own price per share, so a closing cannot be
 * recorded at terms that disagree with what every other investor in that raise is held to.
 */

const STATUS_LABEL: Record<string, string> = {
  pending_signature: 'Awaiting investor signature',
  pending_payment: 'Signed, awaiting funds',
  active: 'Active',
  cancelled: 'Cancelled',
  paid_out: 'Paid out',
  refunded: 'Refunded',
};

const STATUS_TONE: Record<string, string> = {
  pending_signature: 'badge--pending',
  pending_payment: 'badge--pending',
  active: 'badge--ok',
  cancelled: 'badge--muted',
  paid_out: 'badge--ok',
  refunded: 'badge--muted',
};

// ── Recording panel ─────────────────────────────────────────────────────────

function RecordPanel({ onRecorded, onCancel }: { onRecorded: () => void; onCancel: () => void }) {
  const [raises, setRaises] = useState<AdminOpportunity[]>([]);
  const [opportunityId, setOpportunityId] = useState('');
  const [investors, setInvestors] = useState<RecordableInvestor[] | null>(null);
  const [investorUserId, setInvestorUserId] = useState('');
  const [shares, setShares] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listOpportunities()
      // Only a raise that is open or fully subscribed can take a new holding. A draft has
      // not been offered to anyone and a closed one is finished.
      .then((all) => setRaises(all.filter((o) => ['open', 'funded'].includes(o.status))))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load raises.'));
  }, []);

  useEffect(() => {
    if (!opportunityId) { setInvestors(null); return; }
    setInvestors(null);
    setInvestorUserId('');
    listRecordableInvestors(opportunityId)
      .then(setInvestors)
      .catch(() => setInvestors([]));
  }, [opportunityId]);

  const raise = raises.find((r) => r.id === opportunityId) ?? null;
  const shareCount = Number(shares);
  const derivedAmount = raise && Number.isFinite(shareCount) && shareCount > 0
    ? shareCount * raise.pricePerShareCents
    : null;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await recordInvestment({
        opportunityId, investorUserId, shares: shareCount,
        closingNotes: notes.trim() || null,
      });
      onRecorded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record the closing.');
      setBusy(false);
    }
  }

  const ready = opportunityId && investorUserId && shareCount > 0;

  return (
    <section className="adm-card">
      <header className="adm-card__head">
        <h2 className="adm-card__title">Record a closed deal</h2>
        <button type="button" className="button button--ghost adm-btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </header>

      <div className="adm-card__body opp-form">
        {error && <p className="adm-error" role="alert">{error}</p>}

        <label className="field opp-form__wide">
          <span className="field__label">Raise *</span>
          <select className="field__input" value={opportunityId} disabled={busy}
            onChange={(e) => setOpportunityId(e.target.value)}>
            <option value="">Select a raise…</option>
            {raises.map((r) => (
              <option key={r.id} value={r.id}>
                {r.title} | {r.offeringType === '506b' ? 'Rule 506(b)' : 'Rule 506(c)'} |{' '}
                {r.totalShares - r.sharesSold} of {r.totalShares} shares left
              </option>
            ))}
          </select>
        </label>

        <label className="field opp-form__wide">
          <span className="field__label">Investor *</span>
          <select className="field__input" value={investorUserId}
            disabled={busy || !opportunityId || investors == null}
            onChange={(e) => setInvestorUserId(e.target.value)}>
            <option value="">
              {!opportunityId ? 'Pick a raise first' : investors == null ? 'Loading…' : 'Select an investor…'}
            </option>
            {(investors ?? []).map((i) => (
              <option key={i.userId} value={i.userId}>
                {i.email}{i.name ? ` (${i.name})` : ''}
                {i.existingInRaise > 0 ? ` | already holds ${i.existingInRaise} in this raise` : ''}
              </option>
            ))}
          </select>
          {/* The list is the engine's, not every approved investor. For a 506(b) raise it
              contains only investors whose relationship predates the offering. */}
          {investors != null && investors.length === 0 && opportunityId && (
            <span className="opp-reg__hint">
              Nobody can be recorded against this raise yet. For a 506(b) offering only
              investors tagged before it opened are eligible, and that cannot be fixed
              after the fact. Check the 506(b) network screen.
            </span>
          )}
        </label>

        <label className="field">
          <span className="field__label">Shares *</span>
          <input className="field__input mono" type="number" min="1" step="1"
            value={shares} disabled={busy}
            onChange={(e) => setShares(e.target.value)} />
          {raise && (
            <span className="opp-reg__hint">
              Minimum {raise.minimumShares}. At {formatMoney(raise.pricePerShareCents)} per share.
            </span>
          )}
        </label>

        <div className="field">
          <span className="field__label">Amount</span>
          <p className="adm-derived mono">
            {derivedAmount != null ? formatMoney(derivedAmount) : 'Enter a share count'}
          </p>
          <span className="opp-reg__hint">
            Computed from the raise, not entered. This is the only way a closing stays on
            the same terms as the rest of the raise.
          </span>
        </div>

        <label className="field opp-form__wide">
          <span className="field__label">Closing notes</span>
          <textarea className="field__input" rows={2} value={notes} disabled={busy}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="How the deal was reached, wire reference, anything that makes this legible later." />
        </label>

        <p className="opp-reg__hint opp-form__wide">
          Recording reserves the shares and creates the contract. It does not sign
          anything: the investor signs from their own account, and you confirm the funds
          afterwards.
        </p>

        <div className="sup-prompt__actions opp-form__wide">
          <button type="button" className="button button--primary" disabled={!ready || busy}
            onClick={() => void submit()}>
            {busy ? 'Recording…' : 'Record closing'}
          </button>
        </div>
      </div>
    </section>
  );
}

// ── One recorded closing ────────────────────────────────────────────────────

function ClosingRow({ inv, onChanged }: { inv: RecordedInvestment; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<'funds' | 'cancel' | null>(null);
  const [reference, setReference] = useState('');

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setConfirming(null);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="net-row">
      <div className="net-row__main">
        <span className="net-row__email mono">{inv.contractNumber}</span>
        <span className="net-row__name">
          {inv.investor.email} · {inv.shares} shares · {formatMoney(inv.amountInvestedCents)}
        </span>
        <div className="net-row__badges">
          <span className={`badge ${STATUS_TONE[inv.status] ?? 'badge--muted'}`}>
            {STATUS_LABEL[inv.status] ?? inv.status}
          </span>
          <span className="badge badge--muted">{inv.opportunity.title}</span>
          {inv.opportunity.offeringType === '506b' && (
            <span className="badge badge--network">Rule 506(b)</span>
          )}
          {inv.isLegacy && <span className="badge badge--muted">Legacy record</span>}
        </div>
        <span className="opp-reg__hint">
          Recorded {formatDateShort(inv.createdAt)}
          {inv.recordedBy ? ` by ${inv.recordedBy}` : ''}
          {inv.contractSignedAt ? ` · signed ${formatDateTime(inv.contractSignedAt)}` : ''}
          {inv.fundsReceivedAt ? ` · funded ${formatDateTime(inv.fundsReceivedAt)}` : ''}
        </span>
        {inv.closingNotes && <span className="opp-reg__hint">{inv.closingNotes}</span>}
      </div>

      <div className="net-row__actions">
        {inv.hasContract && (
          <a className="adm-link adm-btn-sm" href={`/api/investments/${inv.id}/contract`}>
            Contract
          </a>
        )}
        {inv.canConfirmFunds && (
          <button type="button" className="button button--primary adm-btn-sm" disabled={busy}
            onClick={() => setConfirming('funds')}>
            Confirm funds
          </button>
        )}
        {inv.canCancel && (
          <button type="button" className="button button--ghost adm-btn-sm sup-danger" disabled={busy}
            onClick={() => setConfirming('cancel')}>
            Cancel
          </button>
        )}
      </div>

      {inv.canCountersign && (
        <div className="net-row__panel">
          <p className="opp-reg__hint">
            Waiting on {inv.investor.email} to sign in their own account. There is no way
            to sign for them, by design.
          </p>
        </div>
      )}

      {inv.isLegacy && (
        <div className="net-row__panel">
          <p className="opp-reg__hint">
            Created by the retired self-serve flow, not recorded here. Its status means
            something different from a closing on this screen, so confirming funds or
            cancelling it is not offered. The engine refuses both if asked directly.
          </p>
        </div>
      )}

      {error && <div className="net-row__panel"><p className="adm-error" role="alert">{error}</p></div>}

      {confirming === 'funds' && (
        <div className="net-row__panel">
          <p className="opp-reg__hint">
            Confirming activates this investment. Settlement pays out every active
            holding, so only confirm once the money has actually arrived.
          </p>
          <label className="field">
            <span className="field__label">Wire reference</span>
            <input className="field__input mono" value={reference}
              onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
          </label>
          <div className="sup-prompt__actions">
            <button type="button" className="button button--primary" disabled={busy}
              onClick={() => void run(() => confirmFundsReceived(inv.id, reference || undefined))}>
              {busy ? 'Confirming…' : 'Funds received'}
            </button>
            <button type="button" className="button button--ghost" disabled={busy}
              onClick={() => setConfirming(null)}>Back</button>
          </div>
        </div>
      )}

      {confirming === 'cancel' && (
        <div className="net-row__panel">
          <p className="opp-reg__hint">
            Cancelling releases {inv.shares} shares back to the raise. Only possible before
            funds are confirmed; after that, unwinding is a refund.
          </p>
          <div className="sup-prompt__actions">
            <button type="button" className="button button--ghost sup-danger" disabled={busy}
              onClick={() => void run(() => cancelInvestment(inv.id))}>
              {busy ? 'Cancelling…' : 'Cancel closing'}
            </button>
            <button type="button" className="button button--ghost" disabled={busy}
              onClick={() => setConfirming(null)}>Back</button>
          </div>
        </div>
      )}
    </article>
  );
}

// ── Screen ──────────────────────────────────────────────────────────────────

export function AdminInvestments() {
  const [rows, setRows] = useState<RecordedInvestment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listRecordedInvestments()
      .then((r) => { setRows(r); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load closings.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const awaiting = rows.filter((r) => r.canCountersign || r.canConfirmFunds);

  return (
    <div className="admin-page" data-surface="invest">
      <header className="admin-page__header">
        <div>
          <h1>Investment closings</h1>
          <p className="admin-page__lede">
            Deals agreed off platform, recorded here. The investor signs from their own
            account and you confirm the funds afterwards.
          </p>
        </div>
        {!recording && (
          <button type="button" className="button button--primary"
            onClick={() => setRecording(true)}>
            Record a closing
          </button>
        )}
      </header>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {recording && (
        <RecordPanel
          onCancel={() => setRecording(false)}
          onRecorded={() => { setRecording(false); load(); }}
        />
      )}

      {loading && <p className="admin-page__state">Loading…</p>}

      {!loading && rows.length === 0 && (
        <div className="admin-page__state">
          <p>No closings recorded yet.</p>
        </div>
      )}

      {awaiting.length > 0 && (
        <section className="net-panel">
          <h2 className="net-panel__title">
            Needs attention <span className="net-panel__count mono">{awaiting.length}</span>
          </h2>
          <div className="net-list">
            {awaiting.map((inv) => (
              <ClosingRow key={inv.id} inv={inv} onChanged={load} />
            ))}
          </div>
        </section>
      )}

      {rows.length > 0 && (
        <section className="net-panel">
          <h2 className="net-panel__title">
            All closings <span className="net-panel__count mono">{rows.length}</span>
          </h2>
          <div className="net-list">
            {rows.map((inv) => (
              <ClosingRow key={inv.id} inv={inv} onChanged={load} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
